import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ExternalLink, LogOut, Menu, Wrench, X } from "lucide-react";
import SiteLogo from "@/components/SiteLogo";

const NAV_ITEMS = [
  { to: "/admin/dashboard", label: "Dashboard" },
  { to: "/admin/leads", label: "Leads" },
  { to: "/admin/content", label: "Content" },
  { to: "/admin/maintenance", label: "Mentenanță", icon: Wrench },
  { to: "/admin/json", label: "JSON expert" },
];

const linkClass = ({ isActive }) =>
  `inline-flex h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-[13px] font-medium transition-colors lg:px-4 lg:text-sm ${
    isActive
      ? "bg-[#fbd9e5] text-[#3e2723] shadow-sm"
      : "text-[#3e2723]/70 hover:bg-[#fbd9e5]/45 hover:text-[#3e2723]"
  }`;

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [ready, setReady] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/api/admin/session.php", { credentials: "include" });
        const j = await r.json();
        if (cancelled) return;
        if (!j.ok) {
          navigate("/admin/login", { replace: true });
          return;
        }
        setReady(true);
      } catch {
        if (!cancelled) navigate("/admin/login", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [navOpen]);

  const onLogout = async () => {
    await fetch("/api/admin/logout.php", { method: "POST", credentials: "include" }).catch(() => {});
    navigate("/admin/login", { replace: true });
  };

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f7f4f3] text-sm text-[#3e2723]/65">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-pulse rounded-2xl bg-[#3e2723]/10" />
          <p>Se verifică accesul…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-shell min-h-screen overflow-x-clip bg-[#f7f4f3] text-[#3e2723]">
      <header className="sticky top-0 z-40 border-b border-[#3e2723]/8 bg-white/95 shadow-[0_8px_30px_-20px_rgba(62,39,35,0.15)] backdrop-blur-xl">
        <div className="mx-auto grid h-16 max-w-7xl grid-cols-[1fr_auto] items-center gap-3 px-4 sm:px-6 lg:grid-cols-[minmax(0,auto)_1fr_minmax(0,auto)] lg:gap-6">
          {/* Brand */}
          <div className="flex min-w-0 items-center gap-2.5 lg:col-start-1">
            <div className="flex h-10 shrink-0 items-center">
              <SiteLogo variant="header" />
            </div>
            <span className="hidden rounded-full border border-[#3e2723]/10 bg-[#faf8f8] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#3e2723]/55 sm:inline">
              Admin
            </span>
          </div>

          {/* Desktop navigation — centered, same row */}
          <nav className="hidden min-w-0 items-center justify-center lg:col-start-2 lg:flex" aria-label="Admin principal">
            <div className="inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-full border border-[#3e2723]/10 bg-[#faf8f8]/90 p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
                <NavLink key={to} to={to} className={linkClass}>
                  {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden /> : null}
                  {label}
                </NavLink>
              ))}
            </div>
          </nav>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 lg:col-start-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="hidden h-9 items-center gap-1.5 rounded-full border border-[#3e2723]/12 bg-white px-3.5 text-xs font-semibold text-[#3e2723]/75 transition hover:border-[#3e2723]/20 hover:bg-[#faf8f8] sm:inline-flex"
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              Vezi site
            </a>
            <button
              type="button"
              onClick={onLogout}
              className="hidden h-9 items-center gap-1.5 rounded-full bg-[#3e2723] px-4 text-sm font-semibold text-[#fbd9e5] transition hover:bg-[#2a1a17] sm:inline-flex"
            >
              <LogOut className="h-3.5 w-3.5" aria-hidden />
              Ieșire
            </button>
            <button
              type="button"
              onClick={() => setNavOpen((o) => !o)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#3e2723]/12 bg-white text-[#3e2723] shadow-sm transition hover:bg-[#faf8f8] lg:hidden"
              aria-expanded={navOpen}
              aria-label={navOpen ? "Închide meniul" : "Deschide meniul"}
            >
              {navOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile navigation */}
        {navOpen ? (
          <>
            <button
              type="button"
              className="fixed inset-0 z-40 bg-[#3e2723]/30 backdrop-blur-[2px] lg:hidden"
              aria-label="Închide meniul"
              onClick={() => setNavOpen(false)}
            />
            <nav
              className="relative z-50 border-t border-[#3e2723]/8 bg-white px-4 py-4 shadow-lg lg:hidden"
              aria-label="Admin mobil"
            >
              <div className="mx-auto flex max-w-7xl flex-col gap-1.5">
                {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
                  <NavLink key={to} to={to} className={linkClass} onClick={() => setNavOpen(false)}>
                    {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 opacity-80" aria-hidden /> : null}
                    {label}
                  </NavLink>
                ))}
                <div className="mt-3 grid grid-cols-2 gap-2 border-t border-[#3e2723]/8 pt-3">
                  <a
                    href="/"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-10 items-center justify-center gap-1.5 rounded-full border border-[#3e2723]/12 text-sm font-semibold text-[#3e2723]/75"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Vezi site
                  </a>
                  <button
                    type="button"
                    onClick={onLogout}
                    className="inline-flex h-10 items-center justify-center gap-1.5 rounded-full bg-[#3e2723] text-sm font-semibold text-[#fbd9e5]"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                    Ieșire
                  </button>
                </div>
              </div>
            </nav>
          </>
        ) : null}
      </header>

      <main className="mx-auto w-full max-w-7xl overflow-x-clip px-4 py-6 sm:px-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}
